// src/pages/projects/components/TeamMembersList.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Divider,
  Paper
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon
} from '@mui/icons-material';

import SecureAvatar from '../../../components/ui/SecureAvatar';

/**
 * Displays the list of team members for a project
 */
const TeamMembersList = ({ 
  team = [], 
  canEdit, 
  onAddClick, 
  onEditClick, 
  onRemoveClick,
  currentUserId
}) => {
  // Check if team list is empty
  if (!team || team.length === 0) {
    return (
      <Paper>
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="textSecondary">
            No team members assigned to this project yet.
          </Typography>
          {canEdit && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onAddClick}
              sx={{ mt: 2 }}
            >
              Add Team Member
            </Button>
          )}
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 0 }}>
      <List disablePadding>
        {team.map((member, index) => (
          <React.Fragment key={member._id || index}>
            {index > 0 && <Divider component="li" />}
            <ListItem alignItems="flex-start">
              <ListItemAvatar>
                <SecureAvatar
                  src={member.user?.photo}
                  alt={member.user?.firstName || 'Unknown User'}
                  sx={{ width: 48, height: 48 }}
                >
                  {member.user?.firstName?.[0] || '?'}
                </SecureAvatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Typography variant="subtitle1">
                    {member.user ? 
                      `${member.user.firstName || ''} ${member.user.lastName || ''}`.trim() || 'Unknown User'
                      : 'Unknown User'
                    }
                  </Typography>
                }
                secondary={
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" color="textSecondary" component="span">
                      {member.user?.email || 'No email available'}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                      <Chip
                        label={member.role?.replace(/_/g, ' ')}
                        
                        size="small"
                      />
                      
                      {member.permissions?.canEdit && (
                        <Chip
                          label="Can Edit"
                          variant="outlined"
                          size="small"
                          color="info"
                        />
                      )}
                      
                      {member.permissions?.canManageTasks && (
                        <Chip
                          label="Manage Tasks"
                          variant="outlined"
                          size="small"
                          color="success"
                        />
                      )}
                    </Box>
                  </Box>
                }
              />
              
              {canEdit && member.user?._id !== currentUserId && (
                <ListItemSecondaryAction>
                  <IconButton edge="end" onClick={() => onEditClick(member)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton edge="end" onClick={() => onRemoveClick(member)} color="error">
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              )}
            </ListItem>
          </React.Fragment>
        ))}
      </List>
    </Paper>
  );
};

export default TeamMembersList;